import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FiFileText, FiArrowLeft, FiRefreshCw } from "react-icons/fi";
import toast from "react-hot-toast";
import { getDeletedReports, restoreReport } from "../api/api";
import Pagination from "../components/Pagination";
import "./Reports.css";

export default function DeletedReports() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userName = currentUser?.userName || currentUser?.email?.split("@")[0] || "Manager";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadDeletedReports();
  }, []);

  const loadDeletedReports = async () => {
    setLoading(true);
    try {
      const res = await getDeletedReports();
      const list = res?.data?.data ?? res?.data ?? [];
      setReports(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load deleted reports");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreReport(id);
      toast.success("Report restored successfully");
      setReports((prev) => prev.filter((r) => (r._id ?? r.id) !== id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to restore report");
    }
  };

  // Pagination
  const totalPages = Math.ceil(reports.length / itemsPerPage);
  const paginatedReports = reports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="reports-header">
        <div>
          <button className="back-to-reports-btn" onClick={() => navigate("/reports")}>
            <FiArrowLeft size={18} />
            Back to Reports
          </button>
          <h1>Deleted Reports</h1>
          <p className="reports-subtitle">Restore previously deleted monthly reports</p>
        </div>
        <button className="refresh-btn" onClick={loadDeletedReports}>
          <FiRefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Reports Table */}
      {loading ? (
        <div className="reports-loading">Loading deleted reports...</div>
      ) : reports.length === 0 ? (
        <div className="reports-empty">
          <FiFileText size={48} />
          <h3>No deleted reports found</h3>
          <p>Deleted reports will appear here</p>
        </div>
      ) : (
        <div className="reports-table-wrapper">
          <div className="reports-table-scroll">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Period</th>
                  <th>Author</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Files</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((report) => (
                  <tr key={report._id ?? report.id}>
                    <td>
                      <div className="report-title-cell">
                        <FiFileText size={18} color="#ef4444" />
                        <span className="report-title-text">{report.title}</span>
                      </div>
                    </td>
                    <td>{report.month} {report.year}</td>
                    <td>
                      <div className="report-author">
                        {report.author || report.createdBy?.userName || report.createdBy?.name || "Unknown"}
                      </div>
                    </td>
                    <td>{report.createdAt}</td>
                    <td>
                      <span className={`report-status-badge ${report.status}`}>
                        {report.status}
                      </span>
                    </td>
                    <td>
                      {report.attachments?.length > 0 ? (
                        <span className="report-files-count">
                          {report.attachments.length}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className="report-actions">
                        <button
                          className="report-action-btn restore"
                          onClick={() => handleRestore(report._id ?? report.id)}
                          title="Restore"
                        >
                          <FiRefreshCw size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={setCurrentPage}
      />
    </div>
  );
}
